import {
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TabBase } from '@core/base/tab.base';
import { PendingChanges } from '@core/guards/pending-changes.guard';
import { RestApiService } from '@core/services/rest-api.service';
import { UserRolesAndPermissionsService } from '@core/services/user-roles-and-permissions.service';
import {
  DvCheckboxElement,
  DvDropdownClientSideElement,
  DvDynamicFormElement,
  DvFormUtils,
  FormOf,
} from '@devinco/ui/legacy';
import { RolesAndPermissions } from '@shared/constants/roles-and-permissions';
import { TranslateService } from '@ngx-translate/core';
import { MergeTripTabService } from '@pages/trips-page/services/merge-trip-tab.service';
import TripUtils from '@pages/trips-page/shared/trip.utils';
import { MapMarker, MapInput } from '@features/map/interfaces';
import {
  filter,
  map,
  Observable,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { FormValueType, MergeableTrip, TripDetailsForm } from './interfaces';
import { TripRangeSelectionService } from './trip-range-selection.service';
import { SnackBarConfig } from '@shared/constants/snackbar-config';
import { DvSnackbarService } from '@devinco/ui/dds/snackbar';
import { MapId } from '@features/map/constants/map-id.enum';
import { DvVerticalSplitterComponent } from '@devinco/ui/dds/splitter';
import { SplitterWithGridHelperService } from '@features/vt-grid/services/splitter-with-grid-helper.service';
import { Entity } from '@shared/constants/entity';

@Component({
  selector: 'app-merge-trip-tab',
  templateUrl: './merge-trip-tab.component.html',
  styleUrls: ['./merge-trip-tab.component.scss'],
  providers: [TripRangeSelectionService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MergeTripTabComponent
  extends TabBase
  implements OnInit, OnDestroy, PendingChanges
{
  private api = inject(RestApiService);
  private tabService = inject(MergeTripTabService);
  readonly verticalSplitter = inject(DvVerticalSplitterComponent);
  private formsService = inject(TripRangeSelectionService);
  private snackBar = inject(DvSnackbarService);
  private translate = inject(TranslateService);
  private permissionsService = inject(UserRolesAndPermissionsService);
  private gridEntityHelperService = inject(SplitterWithGridHelperService);

  allTrips = signal<MergeableTrip[]>([]);
  mergeTripElements: DvDynamicFormElement[] = [];
  mergeTripForm: FormGroup<FormOf<FormValueType>> | undefined;
  tripDetailsForm: FormGroup<FormOf<TripDetailsForm>>;
  tripDetailsElements: DvDynamicFormElement[] = [];
  selectedTripsPoints = signal<MapInput[]>([]);
  distance = signal<number | undefined>(undefined);
  selectedTripIds = signal<number[]>([]);
  isTripMerged = signal<boolean>(false);
  isMapVisible$: Observable<boolean>;

  readonly mapId = MapId.MergeTripMap;

  private mergeTripFormSubscription: Subscription | undefined;
  private purposeControlSubscription: Subscription | undefined;
  private destroyRef = inject(DestroyRef);

  constructor() {
    super(inject(ActivatedRoute).parent!);
  }

  ngOnInit(): void {
    this.subscribeToSelectedEntityChanges();
    this.tripDetailsElements = this.tabService.getTripDetailsElements();
    this.tripDetailsForm = DvFormUtils.createFormGroup(
      this.tripDetailsElements
    );
    this.isMapVisible$ = this.permissionsService.hasPermissions([
      RolesAndPermissions.TripShowInMap,
    ]);
  }

  ngOnDestroy(): void {
    this.mergeTripFormSubscription?.unsubscribe();
    this.purposeControlSubscription?.unsubscribe();
  }

  hasChanges = (): boolean => {
    if (!this.tripDetailsForm.valid) return false;
    // for unmerged trips the only meaningful change is a selection beyond the
    // locked anchor; relying on mergeTripForm.dirty would enable save even
    // when the user clicked checkboxes that ended back at the anchor only
    return this.isTripMerged()
      ? !!this.mergeTripForm?.dirty
      : this.selectedTripIds().length > 1;
  };
  pendingChangesSave = (): Observable<boolean> => this.performSave();

  onSave(): void {
    this.performSave().subscribe(() => {});
  }

  private performSave(): Observable<boolean> {
    this.mergeTripForm?.markAsPristine();
    this.tripDetailsForm.markAsPristine();

    if (this.isTripMerged()) {
      return this.splitTrips();
    } else {
      return this.mergeTrips();
    }
  }

  private mergeTrips(): Observable<boolean> {
    return this.api
      .mergeTrips({
        TripIdsToMerge: this.selectedTripIds(),
        Purpose: this.tripDetailsForm.controls.customPurpose?.value,
      })
      .pipe(
        tap(() => {
          this.showSuccessNotification('Trips.MergeTripTab.MergeSuccessful');
          // clear the selection so the pending-changes guard doesn't block
          // the navigation to the newly created merged trip
          this.selectedTripIds.set([]);
        }),
        switchMap(tripId =>
          this.gridEntityHelperService.addNewlyCreatedItemToGrid(
            Entity.Trip,
            tripId
          )
        ),
        tap(() => this.gridEntityHelperService.refreshGrid()),
        map(() => true)
      );
  }

  private splitTrips(): Observable<boolean> {
    return this.entityId$.pipe(
      take(1),
      switchMap(id =>
        this.api.putCustom(`Trip/merge/splittrip/${Number(id)}`).pipe(
          tap(() => {
            this.showSuccessNotification('Trips.MergeTripTab.SplitSuccessful');
          }),
          switchMap(_ =>
            this.gridEntityHelperService.addNewlyCreatedItemToGrid(
              Entity.Trip,
              this.allTrips()[0].TripID
            )
          ),
          tap(() => this.gridEntityHelperService.refreshGrid()),
          map(() => true)
        )
      )
    );
  }

  private showSuccessNotification(
    messageKey: string = 'General.SaveActionSuccessful'
  ): void {
    this.snackBar.open(
      this.translate.instant(messageKey),
      SnackBarConfig.Success
    );
  }

  private subscribeToSelectedEntityChanges(): void {
    this.entityId$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(id => !!id)
      )
      .subscribe(id => {
        this.api.getTrips(id).subscribe(trips => {
          this.clearMergeTripsData();
          this.isTripMerged.set(!!trips.parentTrip?.MergedTrip);
          this.allTrips.set(trips.data);

          this.createTripTableElements(trips.data);
          this.mergeTripForm = DvFormUtils.createFormGroup(
            this.mergeTripElements
          );
          this.listenToPurposeControlValueChanges();

          this.formsService.toggleMergedTripFields(
            this.isTripMerged(),
            this.mergeTripForm,
            this.tripDetailsForm
          );

          if (this.isTripMerged()) {
            this.tripDetailsForm.controls.customPurpose?.setValue(
              trips.parentTrip?.Purpose
            );
            // valueChanges does not fire from initial form values, so the
            // merged-trip case must seed selectedTripIds explicitly to drive
            // distance, start/end details and the map
            const mergedTripIds = trips.data.map(t => t.TripID);
            this.selectedTripIds.set(mergedTripIds);
            this.getTripPoints(mergedTripIds);
          }

          if (
            Object.keys(this.mergeTripForm.value).find(
              tripToMerge => tripToMerge === id
            ) &&
            !this.isTripMerged()
          ) {
            const tripCheckboxControl = this.mergeTripForm.controls[String(id)];
            tripCheckboxControl.setValue(true);

            if (String(id) === this.entityId$.getValue()) {
              tripCheckboxControl.disable();
              // seed map and merging-details for the disabled anchor trip
              const anchorTripId = Number(id);
              this.selectedTripIds.set([anchorTripId]);
              this.getTripPoints([anchorTripId]);
              this.updatePurposeMultiselect();
            }
          }
          this.listenToMergeTripFormChanges();
          this.handleAccessRestrictions();
        });
      });
  }

  private createTripTableElements(trips: MergeableTrip[]): void {
    return trips.forEach(trip => {
      this.mergeTripElements.push(
        new DvCheckboxElement({
          colspan: 12,
          key: String(trip.TripID),
          checkboxLabel: '',
          value: this.isTripMerged(),
        })
      );
    });
  }

  private clearMergeTripsData(): void {
    this.allTrips.set([]);
    this.mergeTripElements = [];
    this.mergeTripForm = new FormGroup({});
    this.distance.set(0);
    this.selectedTripIds.set([]);
    this.tripDetailsForm.reset();
    this.mergeTripFormSubscription?.unsubscribe();
    this.purposeControlSubscription?.unsubscribe();
  }

  private listenToMergeTripFormChanges(): void {
    // seed prevValue so the first user click is diffed against the post-setup
    // state (anchor selected + disabled), not against undefined
    let prevValue: FormValueType | undefined = this.mergeTripForm?.value;
    this.mergeTripFormSubscription = this.mergeTripForm?.valueChanges.subscribe(
      val => {
        const anchorTripId = Number(this.entityId$.getValue());
        const selectedIds = this.formsService.applyRangeSelection(
          this.mergeTripForm!,
          this.allTrips(),
          val,
          prevValue,
          Number.isFinite(anchorTripId) ? anchorTripId : undefined
        );
        prevValue = this.mergeTripForm?.value;

        this.selectedTripIds.set(selectedIds);
        this.selectedTripsPoints.set([]);
        this.getTripPoints(selectedIds);
        this.updatePurposeMultiselect();
      }
    );
  }

  private listenToPurposeControlValueChanges(): void {
    const customPurposeControl = this.tripDetailsForm.controls.customPurpose;
    this.purposeControlSubscription =
      this.tripDetailsForm.controls.purpose?.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(purpose => {
          customPurposeControl?.setValue(purpose?.join(', ') ?? '');
        });
  }

  private calculateDistanceForMergedTrips(selectedTripIds: number[]): string {
    let selectedTripSum = 0;
    selectedTripIds.forEach(id => {
      selectedTripSum +=
        this.allTrips().find(trip => trip.TripID === id)?.Distance || 0;
    });
    this.distance.set(Number(selectedTripSum.toFixed(2)) || 0);
    return String(this.distance());
  }

  private getTripPoints(selectedTripIds: number[]): void {
    if (!selectedTripIds.length) {
      this.distance.set(0);
      return;
    }
    this.api.getTripPoints(selectedTripIds).subscribe(tripPoints => {
      if (!tripPoints?.length) return;

      const tripPointInputs: MapInput[] = [];
      tripPoints.forEach((trip, index) => {
        const tripPointsForSegment: MapMarker[][] =
          TripUtils.mapTripPointsForMap(
            trip.TripPoints,
            index === tripPoints.length - 1
              ? this.calculateDistanceForMergedTrips(selectedTripIds)
              : ''
          );

        tripPointsForSegment.forEach(pointsArr => {
          tripPointInputs.push({ isRouteInput: true, points: pointsArr });
        });
      });
      this.selectedTripsPoints.set(tripPointInputs);
    });
  }

  private updatePurposeMultiselect(): void {
    const items = this.formsService.controlPurposeValueAndItems(
      this.selectedTripIds(),
      this.tripDetailsForm,
      this.allTrips()
    );
    this.tripDetailsElements = [
      {
        ...this.tripDetailsElements[0],
        options: {
          ...this.tripDetailsElements[0].options,
          items,
        },
      } as DvDropdownClientSideElement,
      this.tripDetailsElements[1],
    ];
  }

  private handleAccessRestrictions(): void {
    this.permissionsService
      .hasPermissions([RolesAndPermissions.TripEdit])
      .pipe(take(1))
      .subscribe(hasPermission => {
        if (!hasPermission) {
          this.tripDetailsForm.disable();
          this.mergeTripForm?.disable();
        }
      });
  }
}
